import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsApi } from '../services/settingsApi';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      if (saved) return saved;
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let mounted = true;
    settingsApi
      .themePreferences()
      .then(({ data }) => {
        const savedTheme = data?.find?.((item) => item.key === 'theme')?.value?.theme;
        const localTheme = localStorage.getItem('theme');
        if (mounted && savedTheme && !localTheme) setTheme(savedTheme);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  const toggleTheme = () => {
    setTheme(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      if (isAuthenticated) {
        settingsApi.saveThemePreference(next).catch(() => {});
      }
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
